import React, { useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Portal
} from '@chakra-ui/react';
import {
  MedicalCodeChip,
  MedicalCodeChipProps
} from '@codametrix/ui-components';
import { Card, Conditional } from '@codametrix/ui-components';
import { objectUtils, workBench } from '@codametrix/ui-common';
import classnames from 'classnames';
import * as editorIssues from '../../../common/editor-issues';
import CodeProvenanceChipRow from '../../../code-provenance-chip/code-provenance-chip';
const { getValueAtPath } = workBench?.pathUtils || {};

interface Props {
  chipProps?: MedicalCodeChipProps;
  field?: CMxCommonApp.CodeField;
  value?: object;
  classNamesMedicalCodeChip?: string[];
}

const CodeFieldComponent: React.FC<Props> = ({
  chipProps,
  field,
  value,
  classNamesMedicalCodeChip = []
}) => {
  // controlling this manually so that the popover content will unmount properly when closed
  // otherwise it creates space in the DOM when not visible
  const [isHovered, setIsHovered] = useState(false);

  const getDefaultChipProps = (): MedicalCodeChipProps | undefined => {
    if (!field || !value) return undefined;

    return {
      code: getValueAtPath(value, field.path),
      codeType: field.codeType,
      editable: !value['original'],
      sequence: 0,
      isDraggable: false,
      onDrop: () => {},
      handleCodeDelete: () => field?.onCodeDelete?.(value),
      description: value['description'] ?? undefined,
      codeProvenance: value['codeProvenance'] ?? []
    };
  };

  const computedChipProps = chipProps || getDefaultChipProps();
  if (!computedChipProps) return null; // Render nothing if props are invalid

  const description =
    computedChipProps.description || 'No code description available';
  const popoverModifiers = {
    name: 'offset',
    options: {
      offset: [0, 20] // Adjust horizontal and vertical offset
    }
  };
  const errorMessages = computedChipProps?.codeEdits?.[computedChipProps.code]
    ? editorIssues.formatEditGroup(
        computedChipProps?.codeEdits[computedChipProps.code],
        0
      )
    : false;

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <Popover
      trigger="hover"
      placement="right"
      flip={true}
      modifiers={[popoverModifiers]}
      isOpen={isHovered}
      onOpen={handleMouseEnter}
      onClose={handleMouseLeave}>

              {/* @ts-ignore */}
      <PopoverTrigger>
        <div
          className={classnames(field?.classNames ?? '')}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          <MedicalCodeChip
            {...computedChipProps}
            classNames={classNamesMedicalCodeChip}
          />
        </div>
      </PopoverTrigger>
      <Portal>
        {isHovered && ( //only take up space if it's showing
          <PopoverContent>
            <PopoverArrow />
            <PopoverBody>
              <div className="code-popover-body">
                <b>{objectUtils.capitalize(computedChipProps.codeType)}</b>
                <MedicalCodeChip {...computedChipProps} editable={false} />
                <div className="code-description">{description}</div>
                <CodeProvenanceChipRow
                  codeProvenances={computedChipProps.codeProvenance}
                />
                <Conditional on={computedChipProps.evidence?.length > 0}>
                  <div className="indicator">cmx</div>
                </Conditional>
                <Conditional on={!!errorMessages}>
                  <div className="col-artifacts message-wrap">
                    <div className="col-artifacts col-messages">
                      {errorMessages}
                    </div>
                  </div>
                </Conditional>
              </div>
            </PopoverBody>
          </PopoverContent>
        )}
      </Portal>
    </Popover>
  );
};

export default CodeFieldComponent;
